import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { SafeUrl } from "@angular/platform-browser";
import { gql } from "apollo-angular";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { GetCollectionsQuery, SortOrder } from "../../../common/generated-types";
import { DataService } from "../../providers/data/data.service";
import { Router } from "@angular/router";
import { GET_ACTIVE_CUSTOMER } from "../product-list/product-list.graphql";
import { environment } from "../../../../environments/environment";
import { parseEanList } from "../../../shared/utils/parse-ean-list";

interface LiveOffer {
    name: string;
    ean: string;
    image: string | null;
    offers: string;
    retail: string;
    diff: string;
    price: string;
    showOnLandingPage: boolean;
}

interface FAQ {
    question: string;
    answer: string;
    isOpen: boolean;
}

@Component({
    selector: "vsf-home-page",
    templateUrl: "./home-page.component.html",
    styleUrls: ["./home-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageComponent implements OnInit {
    collections$: Observable<GetCollectionsQuery["collections"]["items"]>;
    heroImage: SafeUrl;
    startSellingUrl: string | null = null;
    liveOffers$: Observable<LiveOffer[]>;
    lastUpdatedText$: Observable<string>;
    isCsvModalVisible = false;
    isLoggedIn$: Observable<boolean>;
    readonly whatsappUrl = "https://wa.me/+37067129200";
    
    stats = [
        { n: "100k+", l: "Products across every category" },
        { n: "500+", l: "Brands you already trust" },
        { n: "100+", l: "Verified suppliers" },
        { n: "€150M+", l: "Inventory available today" },
    ];
    
    partnerLogos = ["Pharmatrade", "NordBeauty", "Lumen", "Vaistinė+", "Cosmetica", "Baltic Retail"];
    
    trustPillars = [
        { icon: "⛨", title: "Payment workflow", body: "Orders follow clear approval and release steps, so buyer and supplier have a traceable record." },
        { icon: "✔", title: "Supplier verification", body: "VAT/KYC details are checked before listing where applicable." },
        { icon: "⇄", title: "Order issue support", body: "Short, late or mismatched orders are documented and handled through support." },
        { icon: "◷", title: "Account support", body: "A named contact helps with sourcing, disputes and pre-orders during business days." },
    ];
    
    steps = [
        { n: "1", title: "Search or upload EANs", body: "Paste EAN codes, upload a list, or search — we match you instantly with top suppliers." },
        { n: "2", title: "Compare offers in real time", body: "See prices, shipping speeds, and supplier ratings side by side in one dashboard." },
        { n: "3", title: "Place a trackable order", body: "Track purchase documents, payment status, delivery progress and issues in one flow." },
    ];
    
    certs = [
        "Documented payment workflow", 
        "VAT/KYC checks where applicable", 
        "Order issue resolution support", 
        "Trackable documents and delivery status"
    ];

    faqs: FAQ[] = [
        {
            question: "What is Venduro, and how does it benefit buyers?",
            answer: "Venduro is a B2B wholesale marketplace for verified European suppliers. Buyers get real-time wholesale pricing, EAN matching, low minimums, and a trackable order flow in one place.",
            isOpen: false
        },
        {
            question: "How can I place and manage my orders?",
            answer: "Search or upload EANs, compare live offers, add products to your cart, and checkout. Purchase documents, payment status, delivery progress and issues stay visible from your account.",
            isOpen: false
        },
        {
            question: "Who can register as a buyer on Venduro?",
            answer: "Venduro is for registered businesses such as pharmacies, retailers and wholesalers. You need a valid company registration to create an account.",
            isOpen: false
        },
        {
            question: "How does the ordering process work?",
            answer: "Compare verified offers, place an order, and follow a documented payment workflow. Status stays visible from quote through delivery, with support if something is short, late or mismatched.",
            isOpen: false
        },
        {
            question: "Does Venduro charge any fees to buyers?",
            answer: "Buyers do not pay a marketplace membership fee. You pay the wholesale price of the products you order, plus any applicable shipping.",
            isOpen: false
        },
        {
            question: "Can I get flexible payment options?",
            answer: "Approved buyers may be offered deferred payment terms. Contact us to discuss credit and payment options for your account.",
            isOpen: false
        }
    ];

    constructor(
        private dataService: DataService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.collections$ = this.dataService
            .query<any>(gql`
                query GetCollectionsList {
                    collections(options: { take: 8 }) {
                        items {
                            id
                            name
                            slug
                            featuredAsset {
                                id
                                preview
                            }
                        }
                    }
                }
            `)
            .pipe(map(({ collections }) => collections.items));

        const products$ = this.dataService
            .query<any>(GET_LANDING_PRODUCTS, {
                options: {
                    filter: {
                        featuredOnLandingPage: { eq: true }
                    },
                    take: 4
                }
            });

        this.liveOffers$ = products$.pipe(
            map((res: any) => {
                const items = res.landingPageProducts?.items || [];
                return items.map((item: any) => {
                    const offersCount = item.offerCount ?? 0;
                    const minPrice = item.bestPriceWithTax ?? 0;
                    const retailPrice = item.retailPriceWithTax ?? 0;
                    const retail = this.formatEuro(retailPrice);
                    return {
                        name: item.name,
                        ean: item.customFields?.gtin || 'N/A',
                        offers: `${offersCount} offers`,
                        retail,
                        diff: retail ? this.formatDiff(retailPrice, minPrice) : '',
                        price: this.formatEuro(minPrice),
                        showOnLandingPage: item.customFields?.showOnLandingPage ?? false,
                        image: this.getAssetUrl(item.featuredAsset?.preview)
                    };
                });
            })
        );

        this.lastUpdatedText$ = products$.pipe(
            map((res: any) => {
                const items = res.landingPageProducts?.items || [];
                if (items.length === 0) return 'Updated just now';
                
                const latestUpdatedAt = items.reduce((latest: Date, item: any) => {
                    const date = new Date(item.updatedAt);
                    return date > latest ? date : latest;
                }, new Date(0));
                
                const diffMs = new Date().getTime() - latestUpdatedAt.getTime();
                const diffMin = Math.floor(diffMs / 60000);
                
                if (diffMin <= 0) {
                    return 'Updated just now';
                } else if (diffMin < 60) {
                    return `Updated ${diffMin} min ago`;
                } else {
                    const diffHours = Math.floor(diffMin / 60);
                    return `Updated ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                }
            })
        );

        this.heroImage = this.getHeroImageUrl();

        this.dataService
            .query<{ storefrontSettings: { startSellingUrl?: string | null } }>(
                GET_STOREFRONT_SETTINGS
            )
            .subscribe(({ storefrontSettings }) => {
                this.startSellingUrl =
                    storefrontSettings?.startSellingUrl ?? null;
            });
            
        this.isLoggedIn$ = this.dataService
            .query(GET_ACTIVE_CUSTOMER)
            .pipe(map((res: any) => !!res.activeCustomer));
    }

    private getHeroImageUrl(): string {
        return `https://eshop.macsales.com/blog/wp-content/uploads/2019/09/Apple_Pro_Display_XDR.jpg`;
    }

    get faqsLeftColumn(): FAQ[] {
        return this.faqs.slice(0, 3);
    }

    get faqsRightColumn(): FAQ[] {
        return this.faqs.slice(3);
    }

    toggleFaq(index: number, column: "left" | "right"): void {
        const actualIndex = column === "left" ? index : index + 3;
        this.faqs[actualIndex].isOpen = !this.faqs[actualIndex].isOpen;
    }

    onSearchEans(eans: string) {
        const parsed = parseEanList(eans);
        if (!parsed) return;
        this.closeCsvModal();
        this.router.navigate(["/search"], {
            queryParams: { search: parsed },
        });
    }

    openCsvModal() {
        this.isCsvModalVisible = true;
    }

    closeCsvModal() {
        this.isCsvModalVisible = false;
    }

    openContactForm(): void {
        const contactSection = document.getElementById("contact-us");
        if (contactSection) {
            const contactButton = contactSection.querySelector("button");
            if (contactButton) {
                contactButton.click();
            }
        }
    }

    onStartSelling(): void {
        if (this.startSellingUrl) {
            const newWindow = window.open(
                this.startSellingUrl,
                "_blank",
                "noopener,noreferrer"
            );
            if (newWindow) {
                newWindow.opener = null;
            }
            return;
        }
        this.openContactForm();
    }

    hasRetailColumn(offers: LiveOffer[]): boolean {
        return offers.length > 0 && offers.every((offer) => !!offer.retail);
    }

    private formatEuro(cents: number): string {
        if (!cents || cents <= 0) {
            return '';
        }
        return `€${(cents / 100).toFixed(2)}`;
    }

    private formatDiff(retailCents: number, bestCents: number): string {
        if (!retailCents || !bestCents || retailCents <= bestCents) {
            return '';
        }
        const percent = Math.round((1 - bestCents / retailCents) * 100);
        if (percent <= 0) {
            return '';
        }
        return `−${percent}%`;
    }

    getAssetUrl(preview: string | null | undefined): string | null {
        if (!preview) return null;
        if (preview.startsWith("http://") || preview.startsWith("https://")) {
            return preview;
        }
        const base = environment.apiPort
            ? `${environment.apiHost}:${environment.apiPort}`
            : environment.apiHost;
        const cleanBase = base.replace(/\/$/, "");
        const cleanPreview = preview.replace(/^\//, "");
        if (cleanPreview.startsWith("assets/")) {
            return `${cleanBase}/${cleanPreview}`;
        }
        return `${cleanBase}/assets/${cleanPreview}`;
    }
}

const GET_COLLECTIONS = gql`
    query GetCollections($options: CollectionListOptions) {
        collections(options: $options) {
            items {
                id
                name
                slug
                parent {
                    id
                    slug
                    name
                }
                featuredAsset {
                    id
                    preview
                }
            }
        }
    }
`;

const GET_STOREFRONT_SETTINGS = gql`
    query GetStorefrontSettings {
        storefrontSettings {
            startSellingUrl
        }
    }
`;

const GET_LANDING_PRODUCTS = gql`
    query GetLandingProducts($options: LandingPageProductListOptions) {
        landingPageProducts(options: $options) {
            items {
                id
                name
                updatedAt
                customFields {
                    gtin
                    showOnLandingPage
                }
                offerCount
                retailPriceWithTax
                bestPriceWithTax
                featuredAsset {
                    id
                    preview
                }
            }
        }
    }
`;
